import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppRoutes } from '../../constants';
import { useAppDispatch, useAppSelector, useGetFiltersFromParams } from '../../hooks';
import { openZeusModal } from '../../redux/reducers';
import { ComponentSize, DropdownPlacement, OrganizationStatus, Permissions, SettingsTab } from '../../types';
import { isFiltersDirty } from '../../utils';
import { ZeusModal } from '../modals';
import {
  Avatar,
  ButtonColor,
  ButtonVariant,
  Divider,
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownTrigger,
  Icon,
  Icons,
  TextButton,
} from '../shared';
import ClearFiltersDialog from './ClearFiltersDialog';
import SidebarButton from './SidebarButton';

const SidebarFooter = () => {
  const { logout } = useAuth0();
  const navigate = useNavigate();

  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  // Redux
  const dispatch = useAppDispatch();
  const { organization, user, isImpersonating } = useAppSelector((state) => state.auth);
  const isTrialOrg = organization?.status === OrganizationStatus.TRIAL;
  const permissions = user?.permissions || [];
  const isSuperAdmin = permissions.includes(Permissions.ADMIN_ACCESS);
  const canOpenZeusModal = isSuperAdmin || isImpersonating;

  const filters = useGetFiltersFromParams();

  // Navigate to the settings page with the active tab.
  const navigateToSettings = useCallback(
    (tab?: SettingsTab) => {
      navigate({ pathname: AppRoutes.SETTINGS + (tab ? '/' + tab : '') });
    },
    [navigate]
  );

  // Close the dropdown after an item is clicked.
  const onDropdownItemClick = useCallback(
    (callback: () => void) => {
      callback();
      setIsAccountModalOpen(false);
    },
    [setIsAccountModalOpen]
  );

  return (
    <div className="flex flex-col gap-6">
      {isFiltersDirty(filters) && (
        <TextButton
          fullWidth
          color={ButtonColor.SECONDARY}
          variant={ButtonVariant.SOFT}
          text="Clear filters"
          onClick={() => setIsConfirmModalOpen(true)}
        />
      )}
      <Divider />
      <Dropdown open={isAccountModalOpen} onOpenChange={setIsAccountModalOpen} placement={DropdownPlacement.RIGHT_END}>
        <DropdownTrigger asChild>
          <SidebarButton
            label={user?.name || ''}
            startElement={<Avatar label={user?.name} imgSrc={user?.picture} size={ComponentSize.X_SMALL} />}
            endElement={<Icons icon={isAccountModalOpen ? Icon.CHEVRON_LEFT : Icon.CHEVRON_RIGHT} />}
            subLabel={organization?.name}
            active={isAccountModalOpen}
            onClick={() => setIsAccountModalOpen(true)}
          />
        </DropdownTrigger>
        <DropdownContent>
          <DropdownItem label="Settings" icon={Icon.SETTINGS} onClick={() => onDropdownItemClick(navigateToSettings)} />
          <DropdownItem
            label="Invite user"
            disabled={isTrialOrg}
            icon={isTrialOrg ? Icon.LOCK : Icon.USER_PLUS}
            onClick={() => onDropdownItemClick(() => navigateToSettings(SettingsTab.PEOPLE))}
          />
          {canOpenZeusModal && (
            <DropdownItem
              label="Zeus mode"
              icon={Icon.ZEUS}
              onClick={() => onDropdownItemClick(() => dispatch(openZeusModal()))}
            />
          )}
          <DropdownItem label="Logout" icon={Icon.LOG_OUT} onClick={logout} />
        </DropdownContent>
      </Dropdown>
      {/* Sidebar modals */}
      <ClearFiltersDialog isOpen={isConfirmModalOpen} setIsOpen={setIsConfirmModalOpen} />
      {/* We conditionally render Zeus modal so we don't make unnecessary/invalid API calls. */}
      {canOpenZeusModal && <ZeusModal />}
    </div>
  );
};

export default SidebarFooter;
